/**
 * Settings > Case Preparation > Filing Packet: the firm's packet defaults
 * (`default_packet_settings`) with a color picker and a live divider
 * preview. Assemble Filing Packet passes the defaults only for a case whose
 * packet settings are still CaseBlink's.
 */
import { Check } from "lucide-react";
import { useState, type FC } from "react";

import {
  casePrepPatch,
  casePrepValue,
  hasCasePrepField
} from "@/api/types/lawFirm";
import {
  paginationPositionLabels,
  type DividerTabTextSize,
  type PaginationPosition
} from "@/api/types/packet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  packetContentsOf,
  packetOrderOf,
  samePacketContents,
  samePacketOrder,
  type PacketContentKind,
  type PacketContents
} from "@/features/caseIde/workflows/packetContents";
import {
  PACKET_SWATCHES,
  TEXT_SIZE_LABELS,
  packetSettingsOf,
  packetStyleOf,
  packetSummary,
  parseHex,
  samePacketStyle,
  type PacketStyle
} from "@/features/caseIde/workflows/packetStyle";
import { cn } from "@/lib/utils";

import {
  CasePrepRow,
  CasePrepSection,
  NeedsBackendNotice
} from "./casePrepParts";
import { useFirmSave, type SectionProps } from "./FirmHouseStyleSection";
import { PacketContentsList } from "./PacketContentsList";

const DIVIDER_LEVELS: { key: keyof PacketStyle; label: string }[] = [
  { key: "mainGroupEnabled", label: "Before Each Folder" },
  { key: "categoryEnabled", label: "Before Each Sub-Folder" },
  { key: "documentEnabled", label: "Before Each Exhibit" }
];

const NO_NUMBERS = "off";

const TITLE_SIZE: Record<DividerTabTextSize, string> = {
  small: "text-sm",
  normal: "text-base",
  large: "text-md"
};

const NUMBER_PLACE: Record<PaginationPosition, string> = {
  bottomRight: "bottom-1.5 right-2",
  bottomLeft: "bottom-1.5 left-2",
  bottomCenter: "bottom-1.5 left-1/2 -translate-x-1/2",
  topRight: "top-1.5 right-2",
  topLeft: "top-1.5 left-2",
  topCenter: "top-1.5 left-1/2 -translate-x-1/2"
};

const PageNumber: FC<{ style: PacketStyle; n: number }> = ({ style, n }) =>
  style.pageNumbers ? (
    <span
      className={cn(
        "absolute font-mono text-sm text-fg-3",
        NUMBER_PLACE[style.position]
      )}
    >
      {n}
    </span>
  ) : null;

export const DividerPreview: FC<{ style: PacketStyle }> = ({ style }) => {
  const anyDivider =
    style.mainGroupEnabled || style.categoryEnabled || style.documentEnabled;
  return (
    <div className="flex items-end gap-3" data-testid="packet-style-preview">
      {anyDivider ? (
        <div
          className="relative flex aspect-[8.5/11] w-28 flex-col items-center justify-center gap-1 rounded-sm px-2 text-center shadow-sm ring-1 ring-line-2"
          style={{ backgroundColor: style.color }}
          data-testid="packet-style-preview-divider"
        >
          <span
            className={cn("font-medium text-fg", TITLE_SIZE[style.textSize])}
          >
            Exhibit 1
          </span>
          <span className="text-sm text-fg-2">Passport</span>
        </div>
      ) : null}
      <div className="relative flex aspect-[8.5/11] w-28 flex-col gap-1 rounded-sm bg-surface p-3 shadow-sm ring-1 ring-line-2">
        <span className="h-1.5 w-3/4 rounded-full bg-fill-2" />
        <span className="h-1.5 w-full rounded-full bg-fill-2" />
        <span className="h-1.5 w-2/3 rounded-full bg-fill-2" />
        <PageNumber style={style} n={12} />
      </div>
    </div>
  );
};

type ColorPickerProps = {
  color: string;
  disabled: boolean;
  onChange: (hex: string) => void;
};

/** The browser's color input, a #RRGGBB field, and the packet presets as quick picks. */
export const DividerColorPicker: FC<ColorPickerProps> = ({
  color,
  disabled,
  onChange
}) => {
  const [draft, setDraft] = useState(color);
  const [synced, setSynced] = useState(color);
  if (synced !== color) {
    setSynced(color);
    setDraft(color);
  }
  const invalid = parseHex(draft) === null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="color"
          aria-label="Tab Color"
          value={color.toLowerCase()}
          disabled={disabled}
          onChange={(e) => onChange(parseHex(e.target.value) ?? color)}
          className="size-8 cursor-pointer rounded-md bg-transparent ring-1 ring-line-2 disabled:cursor-not-allowed"
          data-testid="packet-style-color-input"
        />
        <Input
          aria-label="Tab Color Hex"
          aria-invalid={invalid}
          value={draft}
          disabled={disabled}
          maxLength={7}
          onChange={(e) => {
            setDraft(e.target.value);
            const next = parseHex(e.target.value);
            if (next) onChange(next);
          }}
          onBlur={() => setDraft(color)}
          className="h-8 w-28 font-mono text-base"
          data-testid="packet-style-color-hex"
        />
        <div
          className="flex flex-wrap gap-1"
          role="group"
          aria-label="Preset Colors"
        >
          {PACKET_SWATCHES.map((swatch) => {
            const on = swatch.hex === color;
            return (
              <button
                key={swatch.hex}
                type="button"
                aria-pressed={on}
                aria-label={swatch.name}
                title={swatch.name}
                disabled={disabled}
                onClick={() => onChange(swatch.hex)}
                className={cn(
                  "flex size-5 items-center justify-center rounded-full ring-1 ring-line-2 disabled:cursor-not-allowed",
                  on && "ring-fg"
                )}
                style={{ backgroundColor: swatch.hex }}
                data-testid={`packet-style-color-${swatch.name}`}
              >
                {on ? <Check className="size-3 text-fg" /> : null}
              </button>
            );
          })}
        </div>
      </div>
      {invalid ? (
        <span
          className="text-sm text-danger"
          data-testid="packet-style-color-error"
        >
          Enter a # and six hex digits, like {PACKET_SWATCHES[1].hex}.
        </span>
      ) : null}
    </div>
  );
};

export const PacketStyleSection: FC<SectionProps> = ({
  lawFirmId,
  firm,
  canEdit,
  onSaved
}) => {
  const savedSettings = casePrepValue(firm, "packetSettings");
  const savedStyle = packetStyleOf(savedSettings);
  const savedContents = packetContentsOf(savedSettings);
  const savedOrder = packetOrderOf(savedSettings);
  const settingsSupported = hasCasePrepField(firm, "packetSettings");

  const [style, setStyle] = useState<PacketStyle>(savedStyle);
  const [contents, setContents] = useState<PacketContents>(savedContents);
  const [order, setOrder] = useState<PacketContentKind[]>(savedOrder);
  const [synced, setSynced] = useState(savedSettings);
  if (synced !== savedSettings) {
    setSynced(savedSettings);
    setStyle(savedStyle);
    setContents(savedContents);
    setOrder(savedOrder);
  }
  const { saving, save } = useFirmSave({ lawFirmId, onSaved });

  const styleChanged = !samePacketStyle(style, savedStyle);
  const contentsChanged = !samePacketContents(contents, savedContents);
  const orderChanged = !samePacketOrder(order, savedOrder);
  const changed = styleChanged || contentsChanged || orderChanged;
  const disabled = !canEdit || saving;
  const set = (patch: Partial<PacketStyle>) =>
    setStyle((cur) => ({ ...cur, ...patch }));

  const write = () =>
    void save(
      casePrepPatch("packetSettings", packetSettingsOf(style, contents, order)),
      "Filing packet defaults saved"
    );

  return (
    <CasePrepSection
      title="Filing Packet"
      description="What a new packet includes, and how dividers and page numbers look when a case hasn't set its own. A case can turn each work product off before it builds."
      testId="case-prep-packet"
    >
      <CasePrepRow
        title="Include In New Packets"
        description="Assemble Filing Packet starts with these work products checked, in this order; drag a row to move it. The attorney can uncheck or reorder any of them on the case."
      >
        <PacketContentsList
          order={order}
          contents={contents}
          disabled={disabled}
          onPick={(kind, on) => setContents((cur) => ({ ...cur, [kind]: on }))}
          onReorder={setOrder}
        />
      </CasePrepRow>
      <CasePrepRow title="Divider Pages">
        <fieldset aria-label="Divider Pages" className="flex flex-col gap-1.5">
          {DIVIDER_LEVELS.map((level) => (
            <Field key={level.key} orientation="horizontal">
              <Checkbox
                id={`packet-style-${level.key}`}
                checked={Boolean(style[level.key])}
                disabled={disabled}
                onCheckedChange={(v) => set({ [level.key]: v === true })}
                data-testid={`packet-style-${level.key}`}
              />
              <FieldLabel
                htmlFor={`packet-style-${level.key}`}
                className="text-sm font-normal text-fg-2"
              >
                {level.label}
              </FieldLabel>
            </Field>
          ))}
        </fieldset>
      </CasePrepRow>
      <CasePrepRow
        title="Tab Color"
        control={
          <DividerColorPicker
            color={style.color}
            disabled={disabled}
            onChange={(color) => set({ color })}
          />
        }
      />
      <CasePrepRow
        title="Tab Text Size"
        control={
          <Select
            value={style.textSize}
            onValueChange={(v) => set({ textSize: v as DividerTabTextSize })}
            disabled={disabled}
          >
            <SelectTrigger
              aria-label="Tab Text Size"
              className="w-40"
              data-testid="packet-style-text-size"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" align="end">
              {(Object.keys(TEXT_SIZE_LABELS) as DividerTabTextSize[]).map(
                (size) => (
                  <SelectItem key={size} value={size}>
                    {TEXT_SIZE_LABELS[size]}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        }
      />
      <CasePrepRow
        title="Page Numbers"
        control={
          <Select
            value={style.pageNumbers ? style.position : NO_NUMBERS}
            onValueChange={(v) =>
              v === NO_NUMBERS
                ? set({ pageNumbers: false })
                : set({
                    pageNumbers: true,
                    position: v as PaginationPosition
                  })
            }
            disabled={disabled}
          >
            <SelectTrigger
              aria-label="Page Numbers"
              className="w-40"
              data-testid="packet-style-position"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" align="end">
              <SelectItem value={NO_NUMBERS}>None</SelectItem>
              {(
                Object.keys(paginationPositionLabels) as PaginationPosition[]
              ).map((pos) => (
                <SelectItem key={pos} value={pos}>
                  {paginationPositionLabels[pos]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      {!settingsSupported ? (
        <div className="px-4 py-3">
          <NeedsBackendNotice testId="packet-style-needs-backend" />
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5 px-4 py-3">
        <span className="text-sm text-fg-3">Preview</span>
        <div className="flex justify-center rounded-md bg-fill-1 p-4">
          <DividerPreview style={style} />
        </div>
      </div>
      <CasePrepRow
        title={savedSettings ? "Firm Defaults" : "CaseBlink Defaults"}
        testId="packet-style-status"
        description={`${
          savedSettings
            ? "New packets use"
            : "Until the firm saves its own, packets use"
        } ${packetSummary(savedStyle)}.`}
        control={
          canEdit ? (
            <>
              {changed ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    setStyle(savedStyle);
                    setContents(savedContents);
                    setOrder(savedOrder);
                  }}
                  data-testid="packet-style-discard"
                >
                  Discard
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                disabled={disabled || !settingsSupported || !changed}
                onClick={write}
                data-testid="packet-style-save"
              >
                Save
              </Button>
            </>
          ) : null
        }
      />
    </CasePrepSection>
  );
};
